import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Collapse,
  IconButton,
  InputLabel,
  Link,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  Popover,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import type { Dayjs } from 'dayjs'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useAuthStore } from '@/stores/authStore'
import '@/styles/dashboard.css'
import type { EditorJsData, Homework, User } from '@/types'
import { homeworksApi } from '@/api/homeworks'
import { EditorJsField } from '@/components/editor/EditorJsField'
import { ROUTES } from '@/utils/constants'
import {
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  FilterList as FilterListIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import { usersApi } from '@/api/users'
import { getErrorMessage } from '@/utils/error'
import { alpha, useTheme } from '@mui/material/styles'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'

const emptyEditorJs: EditorJsData = { blocks: [], time: undefined, version: '2.0.0' }

function hasStudentAnswerBlocks(hw: Homework): boolean {
  const blocks = hw.student_answer_json?.blocks
  return Array.isArray(blocks) && blocks.length > 0
}

/** Учитель может открыть проверку, если ДЗ ещё не принято и есть ответ (Editor.js) или отметка «выполнено». */
function canTeacherReview(hw: Homework): boolean {
  if (hw.checked_at) return false
  return hasStudentAnswerBlocks(hw) || Boolean(hw.completed_at)
}

type HomeworkStatusKey = 'checked' | 'revision' | 'completed_waiting' | 'assigned'

/** Учитель: по умолчанию — ДЗ, ожидающие проверки (ученик отметил выполнение). */
const DEFAULT_TEACHER_HOMEWORK_STATUS_FILTER: HomeworkStatusKey[] = ['completed_waiting']

/** Ученик: по умолчанию — «Назначено» и «На доработке». */
const DEFAULT_STUDENT_HOMEWORK_STATUS_FILTER: HomeworkStatusKey[] = ['assigned', 'revision']

function getInitialStatusFilter(): HomeworkStatusKey[] {
  const roles = useAuthStore.getState().user?.roles ?? []
  const teacher = roles.some((r) => r.slug === 'teacher' || r.slug === 'admin')
  return teacher ? [...DEFAULT_TEACHER_HOMEWORK_STATUS_FILTER] : [...DEFAULT_STUDENT_HOMEWORK_STATUS_FILTER]
}

function getHomeworkStatusKey(hw: Homework): HomeworkStatusKey {
  const isChecked = Boolean(hw.checked_at)
  const needsRevision = Boolean(hw.teacher_feedback) && !isChecked
  const isDone = Boolean(hw.completed_at)
  if (isChecked) return 'checked'
  if (needsRevision) return 'revision'
  if (isDone) return 'completed_waiting'
  return 'assigned'
}

/** Ответ: первичная сдача («Назначено») или правки после «На доработке». */
function canStudentSubmitAnswer(hw: Homework): boolean {
  const k = getHomeworkStatusKey(hw)
  return k === 'assigned' || k === 'revision'
}

function formatStudentNameFromRef(s?: Homework['student']): string {
  if (!s) return ''
  const fromRef =
    s.display_name?.trim() ||
    [s.surname, s.first_name].filter((p): p is string => Boolean(p?.trim())).join(' ').trim() ||
    s.email?.trim()
  return fromRef || ''
}

function studentOptionLabel(u: User): string {
  return (
    u.display_name?.trim() ||
    [u.surname, u.first_name].filter((p): p is string => Boolean(p?.trim())).join(' ').trim() ||
    u.email ||
    `#${u.id}`
  )
}

export function HomeworksPage() {
  const { t, i18n } = useTranslation()
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const authUser = useAuthStore((s) => s.user)
  const authUserId = authUser?.id ?? null
  const roles = authUser?.roles ?? []

  const isTeacher = roles.some((r) => r.slug === 'teacher' || r.slug === 'admin')
  const isAdminRole = roles.some((r) => r.slug === 'admin')

  const formatCreatedAt = (iso?: string | null) => {
    if (!iso) return '—'
    dayjs.locale(i18n.language?.startsWith('en') ? 'en' : 'ru')
    return dayjs(iso).format('DD.MM.YYYY HH:mm')
  }
  const actionButtonSx = { textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0 }

  const [homeworks, setHomeworks] = useState<Homework[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<HomeworkStatusKey[]>(getInitialStatusFilter)
  /** Период фильтра по дате создания (YYYY-MM-DD), как на странице уроков */
  const [periodFrom, setPeriodFrom] = useState('')
  const [periodTo, setPeriodTo] = useState('')
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState<HTMLElement | null>(null)
  const [filterStudentIds, setFilterStudentIds] = useState<number[]>([])
  /** По умолчанию — сверху свежие (по дате создания, по убыванию) */
  const [sortKey, setSortKey] = useState<'created_desc' | 'created_asc' | 'assigned_desc' | 'assigned_asc'>('created_desc')
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(15)
  const [error, setError] = useState<string | null>(null)

  // Teacher: list of all students (used to show all homeworks by default)
  const [students, setStudents] = useState<User[]>([])

  const [filtersOpen, setFiltersOpen] = useState(false)

  const [answerOpen, setAnswerOpen] = useState(false)
  /** Editor.js в Dialog ломается при инициализации в скрытом контейнере — монтируем после onEntered */
  const [answerEditorReady, setAnswerEditorReady] = useState(false)
  const [activeHomeworkId, setActiveHomeworkId] = useState<number | null>(null)
  const [answerDraft, setAnswerDraft] = useState<EditorJsData>(emptyEditorJs)
  const [answerSaveError, setAnswerSaveError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const [teacherReviewOpen, setTeacherReviewOpen] = useState(false)
  const [teacherReviewHw, setTeacherReviewHw] = useState<Homework | null>(null)
  const [revisionComment, setRevisionComment] = useState('')
  const [teacherReviewSubmitting, setTeacherReviewSubmitting] = useState(false)
  const [teacherReviewError, setTeacherReviewError] = useState<string | null>(null)

  const openDateRange = (e: MouseEvent<HTMLElement>) => {
    setDateRangeAnchorEl(e.currentTarget)
  }

  const closeDateRange = () => {
    setDateRangeAnchorEl(null)
  }

  const dateCalendarValue: Dayjs | null = periodTo ? dayjs(periodTo) : periodFrom ? dayjs(periodFrom) : null

  const dateRangeLabel = useMemo(() => {
    if (periodFrom && periodTo) {
      return `${dayjs(periodFrom).format('DD.MM.YYYY')} – ${dayjs(periodTo).format('DD.MM.YYYY')}`
    }
    if (periodFrom) {
      return t('homeworks.periodFromOnly', 'с {{date}}', { date: dayjs(periodFrom).format('DD.MM.YYYY') })
    }
    if (periodTo) {
      return t('homeworks.periodToOnly', 'до {{date}}', { date: dayjs(periodTo).format('DD.MM.YYYY') })
    }
    return ''
  }, [periodFrom, periodTo, t])

  const handlePeriodCalendarChange = (value: Dayjs | null) => {
    if (!value) return
    const picked = value.format('YYYY-MM-DD')

    if (!periodFrom || periodTo) {
      setPeriodFrom(picked)
      setPeriodTo('')
      return
    }

    if (picked < periodFrom) {
      setPeriodFrom(picked)
      setPeriodTo(periodFrom)
    } else {
      setPeriodTo(picked)
    }
    closeDateRange()
  }

  const activeHomework = useMemo(() => homeworks.find((h) => h.id === activeHomeworkId) ?? null, [homeworks, activeHomeworkId])

  /** Как на странице уроков: статус (если выбран хотя бы один) + период + ученики. */
  const appliedFiltersCount = useMemo(() => {
    let count = 0
    if (statusFilter.length > 0) count += 1
    if (periodFrom || periodTo) count += 1
    if (isTeacher && filterStudentIds.length > 0) count += 1
    return count
  }, [statusFilter, periodFrom, periodTo, filterStudentIds, isTeacher])

  const hasActiveFilters = appliedFiltersCount > 0

  const studentLabelForHomework = (hw: Homework): string => {
    if (isTeacher) {
      const fromApi = formatStudentNameFromRef(hw.student)
      if (fromApi) return fromApi
      const fromList = students.find((u) => u.id === hw.student_id)
      if (fromList) {
        return (
          fromList.display_name?.trim() ||
          [fromList.surname, fromList.first_name].filter((p): p is string => Boolean(p?.trim())).join(' ').trim() ||
          fromList.email ||
          ''
        )
      }
      return t('homeworks.studentFallback', { id: hw.student_id })
    }
    const self =
      authUser &&
      ([authUser.surname, authUser.first_name].filter((p): p is string => Boolean(p?.trim())).join(' ').trim() ||
        authUser.display_name?.trim() ||
        authUser.email)
    return self || t('homeworks.issuedToYou', 'Вам')
  }

  const filteredHomeworks = useMemo(() => {
    return homeworks.filter((hw) => {
      if (statusFilter.length > 0 && !statusFilter.includes(getHomeworkStatusKey(hw))) return false

      const created = hw.created_at ? dayjs(hw.created_at) : null
      if (periodFrom || periodTo) {
        if (!created?.isValid()) return false
        if (periodFrom && created.isBefore(dayjs(periodFrom).startOf('day'))) return false
        if (periodTo && created.isAfter(dayjs(periodTo).endOf('day'))) return false
      }

      if (isTeacher && filterStudentIds.length > 0 && !filterStudentIds.includes(hw.student_id)) return false

      return true
    })
  }, [homeworks, statusFilter, periodFrom, periodTo, filterStudentIds, isTeacher])

  const sortedHomeworks = useMemo(() => {
    const sortBy = sortKey.startsWith('created') ? 'created_at' : 'assigned_at'
    const sortOrder = sortKey.endsWith('desc') ? 'desc' : 'asc'
    const arr = [...filteredHomeworks]
    const dir = sortOrder === 'asc' ? 1 : -1
    arr.sort((a, b) => {
      const rawA = sortBy === 'created_at' ? a.created_at : a.assigned_at
      const rawB = sortBy === 'created_at' ? b.created_at : b.assigned_at
      const ka = rawA ? dayjs(rawA).valueOf() : 0
      const kb = rawB ? dayjs(rawB).valueOf() : 0
      return (ka - kb) * dir
    })
    return arr
  }, [filteredHomeworks, sortKey])

  const totalFiltered = sortedHomeworks.length

  const paginatedHomeworks = useMemo(() => {
    const start = page * perPage
    return sortedHomeworks.slice(start, start + perPage)
  }, [sortedHomeworks, page, perPage])

  useEffect(() => {
    setPage(0)
  }, [statusFilter, periodFrom, periodTo, filterStudentIds, sortKey])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalFiltered / Math.max(perPage, 1)) - 1)
    if (page > maxPage) setPage(maxPage)
  }, [totalFiltered, perPage, page])

  const fetchStudentHomeworks = async () => {
    if (!authUserId) return
    setLoading(true)
    setError(null)
    try {
      const res = await homeworksApi.getByUser(authUserId)
      setHomeworks(res)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || 'Ошибка загрузки домашних заданий')
    } finally {
      setLoading(false)
    }
  }

  const fetchTeacherFilters = async () => {
    if (!isTeacher) return
    setError(null)
    try {
      const usersRes = await usersApi.getAll({ role: 'student', per_page: 100, page: 1 })
      setStudents(usersRes.data)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || 'Ошибка загрузки фильтров')
    }
  }

  const fetchTeacherHomeworks = async () => {
    if (!authUserId) return
    setLoading(true)
    setError(null)
    try {
      if (students.length === 0) {
        setHomeworks([])
        return
      }

      // Учитель: показываем все домашние задания по всем ученикам.
      const all = (await Promise.all(students.map((s) => homeworksApi.getByUser(s.id)))).flat()
      const uniq = new Map<number, Homework>()
      for (const hw of all) uniq.set(hw.id, hw)
      setHomeworks(Array.from(uniq.values()))
    } catch (e: unknown) {
      setError(getErrorMessage(e) || 'Ошибка загрузки домашних заданий')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!authUserId) return

    if (isTeacher) {
      // filters + then homeworks via dependent effect below
      fetchTeacherFilters().catch(() => {})
      setHomeworks([])
      return
    }

    fetchStudentHomeworks().catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUserId, isTeacher])

  useEffect(() => {
    if (!isTeacher) return
    fetchTeacherHomeworks().catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher, authUserId, students.length])

  const openAnswerModal = (hw: Homework) => {
    if (!canStudentSubmitAnswer(hw)) {
      setError(
        t(
          'homeworks.answerOnlyWhenAllowed',
          'Ответить или исправить ответ можно только в статусе «Назначено» или «На доработке».',
        ),
      )
      return
    }
    setActiveHomeworkId(hw.id)
    setAnswerDraft(hw.student_answer_json ?? emptyEditorJs)
    setAnswerSaveError(null)
    setAnswerEditorReady(false)
    setAnswerOpen(true)
  }

  const handleSaveAnswer = async () => {
    if (!authUserId || !activeHomework) return
    if (!canStudentSubmitAnswer(activeHomework)) {
      setAnswerSaveError(
        t(
          'homeworks.answerOnlyWhenAllowed',
          'Ответить или исправить ответ можно только в статусе «Назначено» или «На доработке».',
        ),
      )
      return
    }
    setSubmitting(true)
    setAnswerSaveError(null)
    try {
      await homeworksApi.answerByUser(authUserId, activeHomework.id, {
        student_answer_json: answerDraft,
        completed_at: new Date().toISOString(),
      })
      setAnswerOpen(false)
      setActiveHomeworkId(null)
      await fetchStudentHomeworks()
    } catch (e: unknown) {
      setAnswerSaveError(getErrorMessage(e) || 'Ошибка сохранения ответа')
    } finally {
      setSubmitting(false)
    }
  }

  const openTeacherReviewModal = (hw: Homework) => {
    setTeacherReviewHw(hw)
    setRevisionComment('')
    setTeacherReviewError(null)
    setTeacherReviewOpen(true)
  }

  const handleTeacherApprove = async () => {
    if (!teacherReviewHw) return
    setTeacherReviewSubmitting(true)
    setTeacherReviewError(null)
    try {
      await homeworksApi.checkByUser(teacherReviewHw.student_id, teacherReviewHw.id, { action: 'approve' })
      setTeacherReviewOpen(false)
      setTeacherReviewHw(null)
      await fetchTeacherHomeworks()
    } catch (e: unknown) {
      setTeacherReviewError(getErrorMessage(e) || 'Ошибка проверки домашнего задания')
    } finally {
      setTeacherReviewSubmitting(false)
    }
  }

  const handleTeacherRevision = async () => {
    if (!teacherReviewHw) return
    const c = revisionComment.trim()
    if (!c) {
      setTeacherReviewError(t('homeworks.revisionCommentRequired', 'Укажите комментарий для возврата на доработку'))
      return
    }
    setTeacherReviewSubmitting(true)
    setTeacherReviewError(null)
    try {
      await homeworksApi.checkByUser(teacherReviewHw.student_id, teacherReviewHw.id, {
        action: 'revision',
        comment: c,
      })
      setTeacherReviewOpen(false)
      setTeacherReviewHw(null)
      await fetchTeacherHomeworks()
    } catch (e: unknown) {
      setTeacherReviewError(getErrorMessage(e) || 'Ошибка возврата на доработку')
    } finally {
      setTeacherReviewSubmitting(false)
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 }, pb: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('homeworks.title', 'Домашние задания')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('homeworks.title', 'Домашние задания')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <PageHelpPopover pageKey="homeworks" />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: isMobile ? 200 : 280 }}>
            <InputLabel>{t('homeworks.sortBy', 'Сортировка')}</InputLabel>
            <Select
              value={sortKey}
              label={t('homeworks.sortBy', 'Сортировка')}
              onChange={(e) => setSortKey(e.target.value as typeof sortKey)}
            >
              <MenuItem value="created_desc">{t('homeworks.sort_created_at_desc', 'Сначала новые (по дате создания)')}</MenuItem>
              <MenuItem value="created_asc">{t('homeworks.sort_created_at_asc', 'Сначала старые (по дате создания)')}</MenuItem>
              <MenuItem value="assigned_desc">{t('homeworks.sort_assigned_at_desc', 'Сначала новые (по дате выдачи)')}</MenuItem>
              <MenuItem value="assigned_asc">{t('homeworks.sort_assigned_at_asc', 'Сначала старые (по дате выдачи)')}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Button
              size="small"
              variant={filtersOpen ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((o) => !o)}
              sx={{ height: 40 }}
            >
              {t('homeworks.filtersButton', 'Фильтры')}
            </Button>
            {hasActiveFilters && (
              <Box
                aria-label={t('homeworks.filtersBadgeActive', 'Активные фильтры: {{count}}', { count: appliedFiltersCount })}
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  height: 20,
                  minWidth: 20,
                  px: 0.75,
                  borderRadius: '999px',
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: (t2) => t2.shadows[2],
                  pointerEvents: 'none',
                }}
              >
                {appliedFiltersCount}
              </Box>
            )}
          </Box>
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small"
                label={t('homeworks.filterPeriod', 'Период')}
                value={dateRangeLabel}
                placeholder={t('homeworks.filterPeriodPlaceholder', 'Все даты')}
                InputProps={{ readOnly: true }}
                onClick={openDateRange}
                sx={{ minWidth: { xs: '100%', sm: 240 }, cursor: 'pointer' }}
              />
              <Popover
                open={Boolean(dateRangeAnchorEl)}
                anchorEl={dateRangeAnchorEl}
                onClose={closeDateRange}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Box sx={{ p: 1 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                    <DateCalendar
                      value={dateCalendarValue}
                      views={['month', 'day']}
                      onChange={(v) => void handlePeriodCalendarChange(v)}
                    />
                  </LocalizationProvider>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        setPeriodFrom('')
                        setPeriodTo('')
                        closeDateRange()
                      }}
                    >
                      {t('common.reset', 'Сбросить')}
                    </Button>
                    <Button size="small" variant="contained" color="secondary" onClick={closeDateRange}>
                      {t('common.ok', 'Ок')}
                    </Button>
                  </Box>
                </Box>
              </Popover>
              {isTeacher ? (
                  <Autocomplete
                    multiple
                    size="small"
                    sx={{ minWidth: 280, flex: isMobile ? 1 : undefined }}
                    options={students}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    getOptionLabel={(u) => studentOptionLabel(u)}
                    value={students.filter((s) => filterStudentIds.includes(s.id))}
                    onChange={(_, v) => setFilterStudentIds(v.map((u) => u.id))}
                    renderInput={(params) => (
                      <TextField {...params} label={t('homeworks.filterStudent', 'Ученик')} placeholder={t('homeworks.filterStudentSearch', 'Поиск')} />
                    )}
                  />
                ) : null}
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel>{t('homeworks.filterStatus', 'Статус')}</InputLabel>
                  <Select
                    label={t('homeworks.filterStatus', 'Статус')}
                    multiple
                    value={statusFilter}
                    onChange={(e) => {
                      const v = e.target.value
                      if (typeof v === 'string') {
                        setStatusFilter(v ? ([v] as HomeworkStatusKey[]) : [])
                        return
                      }
                      setStatusFilter(v as HomeworkStatusKey[])
                    }}
                    renderValue={(selected) => {
                      const arr = selected as HomeworkStatusKey[]
                      if (arr.length === 0) return t('homeworks.filterAllStatuses', 'Все статусы')
                      return arr
                        .map((k) => {
                          switch (k) {
                            case 'checked':
                              return t('homeworks.statusChecked')
                            case 'revision':
                              return t('homeworks.statusRevision')
                            case 'completed_waiting':
                              return t('homeworks.statusCompleted')
                            default:
                              return t('homeworks.statusAssigned')
                          }
                        })
                        .join(', ')
                    }}
                  >
                    <MenuItem value="assigned">{t('homeworks.statusAssigned')}</MenuItem>
                    <MenuItem value="completed_waiting">{t('homeworks.statusCompleted')}</MenuItem>
                    <MenuItem value="revision">{t('homeworks.statusRevision')}</MenuItem>
                    <MenuItem value="checked">{t('homeworks.statusChecked')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
        </Collapse>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card sx={{ p: 0, overflow: 'hidden' }}>
            {loading ? (
              <Box sx={{ p: 0 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            ) : totalFiltered === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  {t('homeworks.empty', 'Нет заданий')}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('homeworks.tableCreatedAt', 'Дата создания')}</TableCell>
                      <TableCell>{t('homeworks.tableTask')}</TableCell>
                      {isTeacher ? (
                        <TableCell>{t('homeworks.tableStudent', 'Ученик')}</TableCell>
                      ) : null}
                      <TableCell>{t('homeworks.tableStatus', 'Статус')}</TableCell>
                      <TableCell align="right">{t('common.actions', 'Действия')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedHomeworks.map((hw) => {
                    const isDone = Boolean(hw.completed_at)
                    const isChecked = Boolean(hw.checked_at)
                    const needsRevision = Boolean(hw.teacher_feedback) && !isChecked
                    return (
                      <TableRow key={hw.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {formatCreatedAt(hw.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {hw.lesson_id ? `Lesson #${hw.lesson_id}` : `HW #${hw.id}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                            {hw.task_text ?? ''}
                          </Typography>
                        </TableCell>
                        {isTeacher ? (
                          <TableCell>
                            <Link
                              component={RouterLink}
                              to={
                                isAdminRole
                                  ? ROUTES.ADMIN_USER_VIEW(hw.student_id)
                                  : `/users/${hw.student_id}`
                              }
                              underline="hover"
                              sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                            >
                              {studentLabelForHomework(hw)}
                            </Link>
                          </TableCell>
                        ) : null}
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              isChecked
                                ? t('homeworks.statusChecked')
                                : needsRevision
                                  ? t('homeworks.statusRevision')
                                  : isDone
                                    ? t('homeworks.statusCompleted')
                                    : t('homeworks.statusAssigned')
                            }
                            sx={{
                              borderRadius: 999,
                              fontWeight: 700,
                              ...(isChecked
                                ? { bgcolor: alpha(theme.palette.success.main, 0.14), color: theme.palette.success.main }
                                : needsRevision
                                  ? { bgcolor: alpha(theme.palette.warning.main, 0.18), color: theme.palette.warning.dark }
                                  : isDone
                                    ? { bgcolor: alpha(theme.palette.primary.main, 0.14), color: theme.palette.primary.main }
                                    : { bgcolor: 'action.disabledBackground', color: 'text.primary' }),
                            }}
                          />
                          {needsRevision && hw.teacher_feedback && (
                            <Typography variant="body2" color="warning.dark" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                              {t('homeworks.teacherFeedback', 'Комментарий учителя')}: {hw.teacher_feedback}
                            </Typography>
                          )}
                          {hw.student_answer_text && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                              {t('homeworks.answer', 'Ответ')}: {hw.student_answer_text}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            {isTeacher ? (
                              canTeacherReview(hw) ? (
                                <Tooltip title={t('homeworks.checkAction', 'Проверить')}>
                                  <IconButton
                                    size="small"
                                    aria-label={t('homeworks.checkAction', 'Проверить')}
                                    onClick={() => openTeacherReviewModal(hw)}
                                  >
                                    <FactCheckIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : null
                            ) : canStudentSubmitAnswer(hw) ? (
                              <Button
                                size="small"
                                variant="outlined"
                                sx={actionButtonSx}
                                onClick={() => openAnswerModal(hw)}
                              >
                                {getHomeworkStatusKey(hw) === 'revision'
                                  ? t('homeworks.answerActionRevision', 'Исправить ответ')
                                  : t('homeworks.answerAction')}
                              </Button>
                            ) : null}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
        </Card>

        {!loading && totalFiltered > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              mt: 2,
              ...(isMobile && { flexDirection: 'column', alignItems: 'stretch' }),
            }}
          >
            <TablePagination
              component="div"
              count={totalFiltered}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => {
                setPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage={isMobile ? '' : t('words.rows_per_page')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${t('words.of')} ${count !== -1 ? count : `${t('words.more_than')} ${to}`}`
              }
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'auto',
                ...(isMobile && { borderTop: 1, borderColor: 'divider', pt: 1 }),
              }}
            />
          </Box>
        )}
      </Box>

      <Dialog
        open={answerOpen}
        onClose={() => setAnswerOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          transition: {
            onEntered: () => setAnswerEditorReady(true),
            onExited: () => setAnswerEditorReady(false),
          },
        }}
      >
        <DialogTitle>
          {activeHomework && getHomeworkStatusKey(activeHomework) === 'revision'
            ? t('homeworks.answerDialogTitleRevision', 'Исправление ответа')
            : t('homeworks.answerDialogTitle')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1, minHeight: 320 }}>
          {activeHomework && (
            <>
              {getHomeworkStatusKey(activeHomework) === 'revision' && activeHomework.teacher_feedback && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700 }}>
                    {t('homeworks.teacherFeedback', 'Комментарий учителя')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {activeHomework.teacher_feedback}
                  </Typography>
                </Alert>
              )}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('homeworks.tableTask')}
              </Typography>
              <Box
                component="section"
                aria-label={t('homeworks.tableTask')}
                sx={{
                  mb: 2.5,
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                {activeHomework.task_text?.trim() ? (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {activeHomework.task_text}
                  </Typography>
                ) : answerEditorReady ? (
                  <EditorJsField
                    key={`hw-task-${activeHomeworkId}`}
                    value={activeHomework.task_json ?? emptyEditorJs}
                    onChange={() => {}}
                    readOnly
                    readOnlySurface="paper"
                    hideLabel
                    rows={6}
                    containerSx={{ mt: 0 }}
                    holderSx={{ border: 'none', borderRadius: 0, p: 0 }}
                    initDelayMs={0}
                  />
                ) : (
                  <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
                )}
              </Box>

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('homeworks.solutionLabel')}
              </Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
                {t('homeworks.answerDialogHint')}
              </Typography>
              {!answerEditorReady ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} aria-label={t('common.loading')} />
                </Box>
              ) : (
                <EditorJsField
                  key={activeHomeworkId ?? 'answer'}
                  value={answerDraft}
                  onChange={setAnswerDraft}
                  readOnly={false}
                  hideLabel
                  label=""
                  rows={10}
                  containerSx={{ mt: 0 }}
                  initDelayMs={80}
                />
              )}
              {answerSaveError && (
                <Typography color="error.main" variant="body2" sx={{ mt: 1 }}>
                  {answerSaveError}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnswerOpen(false)} disabled={submitting}>
            {t('common.cancel', 'Отмена')}
          </Button>
          <Button variant="contained" onClick={handleSaveAnswer} disabled={submitting}>
            {t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={teacherReviewOpen}
        onClose={() => {
          if (!teacherReviewSubmitting) {
            setTeacherReviewOpen(false)
            setTeacherReviewHw(null)
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('homeworks.teacherReviewTitle', 'Проверка домашнего задания')}</DialogTitle>
        <DialogContent>
          {teacherReviewHw && (
            <>
              {teacherReviewHw.task_text ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  <strong>{t('homeworks.tableTask')}:</strong> {teacherReviewHw.task_text}
                </Typography>
              ) : null}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('homeworks.studentAnswerLabel', 'Ответ ученика')}
              </Typography>
              <EditorJsField
                value={teacherReviewHw.student_answer_json ?? emptyEditorJs}
                onChange={() => {}}
                readOnly
                hideLabel
                rows={10}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                sx={{ mt: 2 }}
                label={t('homeworks.revisionCommentLabel', 'Комментарий при возврате на доработку')}
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
                disabled={teacherReviewSubmitting}
                placeholder={t('homeworks.revisionCommentPlaceholder', 'Обязательно для кнопки «На доработку»')}
              />
              {teacherReviewError && (
                <Typography color="error.main" variant="body2" sx={{ mt: 1 }}>
                  {teacherReviewError}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={() => {
              if (!teacherReviewSubmitting) {
                setTeacherReviewOpen(false)
                setTeacherReviewHw(null)
              }
            }}
            disabled={teacherReviewSubmitting}
          >
            {t('common.cancel', 'Отмена')}
          </Button>
          <Button
            variant="outlined"
            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0 }}
            onClick={handleTeacherRevision}
            disabled={teacherReviewSubmitting || !revisionComment.trim()}
          >
            {t('homeworks.sendToRevision', 'На доработку')}
          </Button>
          <Button
            variant="contained"
            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0 }}
            onClick={handleTeacherApprove}
            disabled={teacherReviewSubmitting}
          >
            {t('homeworks.approveHomework', 'Завершить')}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  )
}

